import React, { useEffect, useRef } from "react";

export interface EventStatusUpdate {
  uuid: string;
  newStatus: string;
}

export type SetEventsFn<T extends { uuid: string; status?: string }> =
  React.Dispatch<React.SetStateAction<T[]>>;

// Hook applies queued status updates in batches on an interval
// Consumers pass their own queue ref to push updates into
export const useEventsStatusQueue = <
  T extends { uuid: string; status?: string }
>(
  setEvents: SetEventsFn<T>,
  eventQueueRef: React.MutableRefObject<EventStatusUpdate[]>,
  intervalMs: number = 200
): void => {
  const queueRef = eventQueueRef;

  useEffect(() => {
    const interval = setInterval(() => {
      if (queueRef.current.length > 0) {
        setEvents((prev) => {
          const merged: T[] = [...prev];
          for (const update of queueRef.current) {
            const idx = merged.findIndex(
              (record) => record.uuid === update.uuid
            );
            if (idx >= 0) {
              merged[idx] = { ...merged[idx], status: update.newStatus } as T;
            }
          }
          return merged;
        });

        queueRef.current = [];
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [intervalMs, setEvents]);
};
